import { useEffect, useId, useRef } from 'react';
import { HiX } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

/**
 * Armazón común de las ventanas de producto: fondo atenuado, cabecera fija,
 * cuerpo con desplazamiento y pie fijo.
 *
 * No sustituye a NoticeModal. Un aviso exige pulsar OK y por eso no se cierra
 * solo ni con Esc; una ventana de consulta como esta sí, porque cerrarla no
 * pierde nada.
 */
export default function ModalShell({ title, onClose, footer, maxWidth = 'max-w-4xl', children }) {
    const { t } = useTranslation();
    const tituloId = useId();
    const alCerrar = useRef(onClose);
    alCerrar.current = onClose;

    useEffect(() => {
        const teclado = (e) => {
            if (e.key === 'Escape') alCerrar.current();
        };
        document.addEventListener('keydown', teclado);

        // Sin esto, la rueda del ratón desplaza la página de detrás.
        const anterior = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', teclado);
            document.body.style.overflow = anterior;
        };
    }, []);

    return (
        // El fondo no cierra. Un clic despistado fuera de la ventana no puede
        // hacer perder la ficha que estabas leyendo: se sale por la X, por
        // Cerrar o con Esc, que son tres gestos deliberados.
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={tituloId}
                className={`bg-white dark:bg-slate-800 rounded-2xl ${maxWidth} w-full max-h-[90vh] overflow-y-auto shadow-2xl`}
            >
                <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
                    <h2 id={tituloId} className="text-xl font-bold text-slate-900 dark:text-white">
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <HiX className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                <div className="p-6 space-y-6">{children}</div>

                {footer && (
                    <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-6 py-4">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
